import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
/**
 * Patient Visit Context read model (ADR-0003, FOUNDATION-SPEC §11.2) —
 * the compact, versioned, preassembled patient context returned by
 * api.get_visit_context (catalog contract patient.get_visit_context.v1).
 *
 * Consumers treat sections as data with provenance: every fact carries a
 * reference to its source row (type, id, version, content hash) so nothing
 * asserted about the chart is ever untraceable. Section payloads are parsed
 * tolerantly — the authoritative shape lives in the database assembler, and
 * additive changes there must not break older consumers.
 */
export const VisitContextSectionKeySchema = z.enum([
    "demographics",
    "pmh",
    /** Alias of `pmh` kept for one release (20260906231050); prefer `pmh`. */
    "problems",
    "recent_encounter_diagnoses",
    "allergies",
    "medications",
    "family_history",
    "social_history",
    "immunizations",
    "vitals",
    "recent_results",
    "recent_encounters",
    "open_work",
    "consents",
    "preferences",
    "conflicts",
    "risk_flags",
    "consult_notes",
    "imaging_reports",
    "incoming_reports",
    "document_assertions",
    /** Visits imported from a previous EMR: history, never encounters here. */
    "legacy_visits",
]);
export const VisitContextProvenanceSchema = z
    .object({
    sourceType: z.string().min(1).max(80),
    sourceId: IdSchema,
    version: z.coerce.number().int().positive().optional(),
    contentHash: Sha256Schema.optional(),
})
    .passthrough();
export const VisitContextStalenessSchema = z
    .object({
    fingerprintFresh: z.boolean(),
    minimumWatermarkSatisfied: z.boolean(),
    pinned: z.boolean(),
})
    .passthrough();
export const VisitContextSectionsSchema = z.record(z.string(), z.unknown());
/**
 * The sections a model prompt receives. `api.get_visit_context` serves
 * `problems` as an alias of `pmh` for one release (20260906231050) so include
 * lists written against the old key keep working, but a prompt must never
 * carry the past medical history twice: the alias is dropped whenever `pmh`
 * is present. A context stored before that release carries only `problems`,
 * which then stays as the model's PMH input.
 */
export function promptVisitContextSections(sections) {
    if (!("pmh" in sections) || !("problems" in sections))
        return sections;
    return Object.fromEntries(Object.entries(sections).filter(([key]) => key !== "problems"));
}
export const VisitContextSchema = z
    .object({
    contextVersion: z.coerce.number().int().positive(),
    watermark: z.coerce.number().int().nonnegative(),
    contentHash: Sha256Schema,
    assembledAt: IsoDateTimeSchema,
    tier: z.enum(["full", "core"]),
    tokenEstimate: z.coerce.number().int().nonnegative(),
    staleness: VisitContextStalenessSchema,
    sections: VisitContextSectionsSchema,
})
    .passthrough();
/**
 * Compact reference to one assembled context version. This is what cache
 * keys, agent-run context manifests, and branch packages carry instead of
 * the context body.
 */
export const VisitContextRefSchema = z
    .object({
    contextVersion: z.coerce.number().int().positive(),
    watermark: z.coerce.number().int().nonnegative(),
    contentHash: Sha256Schema,
})
    .strict();
export function visitContextRef(context) {
    return {
        contextVersion: context.contextVersion,
        watermark: context.watermark,
        contentHash: context.contentHash,
    };
}
export const VisitContextRequestOptionsSchema = z
    .object({
    encounterId: IdSchema.optional(),
    include: z.array(VisitContextSectionKeySchema).min(1).optional(),
    minimumWatermark: z.number().int().nonnegative().optional(),
    contextVersion: z.number().int().positive().optional(),
})
    .strict();
/**
 * Targeted-read escape hatch (plan Phase 8, api.read_visit_source): the
 * exact source a condensed context references by provenance, read verbatim
 * for the clinician. Display-only — it never joins the model context, which
 * stays the stored, version-pinned bytes.
 */
export const VisitSourceRefSchema = z
    .object({
    sourceType: z.enum(["note", "result", "incoming_report", "legacy_visit"]),
    sourceId: IdSchema,
    version: z.number().int().positive(),
})
    .strict();
export const VisitSourceContentSchema = z
    .object({
    sourceType: z.enum(["note", "result", "incoming_report", "legacy_visit"]),
    sourceId: IdSchema,
    version: z.coerce.number().int().positive(),
    contentHash: Sha256Schema,
    /** Note reads: the verbatim SOAP sections of the signed note. */
    sections: z.record(z.string(), z.string()).optional(),
    /** Result reads: the verbatim report payload. */
    payload: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
//# sourceMappingURL=visit-context.js.map